import React, { useEffect, useMemo, useContext } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import { Loading, Icon } from 'components';
import { ListContext } from 'contexts/ListContext';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import './index.scss';
interface TableProps {
	cols: Array<any>;
	data: any;
	fetchData: Function;
	loading?: boolean;
	pageCount?: any;
	totalItems?: number;
	removeFunction?: Function;
	noDataMsg: string;
}

export const Table = ({
	cols,
	data,
	fetchData,
	loading,
	pageCount: controlledPageCount,
	totalItems,
	noDataMsg,
	removeFunction,
}: TableProps) => {
	const columns = useMemo(() => cols, []);
	const { t } = useTranslation();
	const {
		orderBy,
		fetching,
		setOrderBy,
		setFilterText,
		listData,
		setListData,
	} = useContext<any>(ListContext);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		pageOptions,
		state: { pageSize },
		prepareRow,
	} = useTable(
		{
			columns,
			data,
			manualPagination: true,
			pageCount: controlledPageCount,
		},
		usePagination
	);

	useEffect(() => {
		setListData({ ...listData, pageSize });
	}, [pageSize]);

	const handleOnRemove = ({ original }: any) => {
		removeFunction && removeFunction(original);
	};

	const handleOnHeaderClick = (col: any) => {
		console.log({ col });
		if (col?.canSort) {
			setOrderBy({ id: col.id, desc: !orderBy?.desc });
		}
	};

	return data.length === 0 && !loading ? (
		<p className='relative block w-full text-center text-orange'>
			{noDataMsg ? noDataMsg : t('noData')}
		</p>
	) : fetching ? (
		<Loading />
	) : (
		<>
			<div className='overflow-x-scroll border border-grey-light rounded-lg '>
				<table
					{...getTableProps()}
					className='w-full'
				>
					<thead className='table-head'>
						{
							// Loop over the header rows
							headerGroups.map((headerGroup: any) => (
								// Apply the header row props
								<tr {...headerGroup.getHeaderGroupProps()}>
									{
										// Loop over the headers in each row
										headerGroup.headers.map((column: any) => (
											// Apply the header cell props
											<th
												{...column.getHeaderProps()}
												onClick={() => handleOnHeaderClick(column)}
												className={cn(
													'px-4 py-3 text-[10px] font-semi-bold text-left uppercase text-grey-dark whitespace-nowrap',
													{
														'cursor-pointer': column.canSort,
														'order-by': orderBy?.id === column.id,
														'sort-desc':
															orderBy?.id === column.id && orderBy?.desc,
														'sort-asc':
															orderBy?.id === column.id && !orderBy?.desc,
													}
												)}
											>
												<span>
													{
														// Render the header
														column.render('Header')
													}
												</span>
											</th>
										))
									}
									{removeFunction && <th></th>}
								</tr>
							))
						}
					</thead>
					<tbody
						{...getTableBodyProps()}
						className='bg-white'
					>
						{page.map((row: any) => {
							prepareRow(row);
							return (
								// Apply the row props
								<tr
									className='border-b border-grey-light'
									{...row.getRowProps()}
								>
									{
										// Loop over the rows cells
										row.cells.map((cell: any) => {
											// Apply the cell props
											return (
												<td
													className='text-xs text-grey-dark p-4'
													{...cell.getCellProps()}
												>
													{
														// Render the cell contents
														cell.render('Cell')
													}
												</td>
											);
										})
									}
									{removeFunction && (
										<td>
											<Icon
												name='Trash'
												className='relative block mx-2'
												onClick={() => handleOnRemove(row)}
											/>
										</td>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<div className='mt-6 mx-auto relative flex items-center justify-between'>
				{controlledPageCount > 0 && (
					<div className='flex items-center'>
						<button
							className={cn('mr-4', {
								'opacity-50': listData.currentPage === 0,
							})}
							disabled={listData.currentPage === 0}
							onClick={() =>
								setListData({
									...listData,
									currentPage: listData.currentPage - 1,
								})
							}
						>
							<Icon
								name='BackArrow'
								width={18}
							/>
						</button>
						<span className='relative block text-xs'>
							{t('page')}{' '}
							<span className='text-orange'>{listData?.currentPage + 1}</span>{' '}
							{t('of')} {pageOptions.length}
						</span>
						<button
							className={cn('ml-4', {
								'opacity-50': listData.currentPage + 1 === listData.totalPages,
							})}
							disabled={listData.currentPage + 1 === listData.totalPages}
							onClick={() =>
								setListData({
									...listData,
									currentPage: listData.currentPage + 1,
								})
							}
						>
							<Icon
								name='NextArrowOrange'
								width={18}
							/>
						</button>
					</div>
				)}
				<div>
					{loading ? (
						<Loading />
					) : (
						totalItems &&
						totalItems > page.length && (
							<p className='text-xs text-grey-text'>
								{t('showing')} {page.length} {t('of')} {totalItems}
							</p>
						)
					)}
				</div>
			</div>
		</>
	);
};
